/* eslint-disable lingui/no-unlocalized-strings */
import mixpanel from "mixpanel-browser";

import { logger } from "@/common/services/logger";

const EventName = {
  MarketplaceItemClicked: "Marketplace Item Clicked",
  NewStorageClicked: "New Storage Clicked",
  QuarterClicked: "Quarter Clicked",
  StorageClicked: "Storage Clicked",
  TradingOrderClicked: "Trading Order Clicked",
  TradingSellCancelled: "Trading Sell Cancelled",
  TradingSellClicked: "Trading Sell Clicked",
  TradingSellConfirmed: "Trading Sell Confirmed",
} as const;

type EventNameType = (typeof EventName)[keyof typeof EventName];

const isMixpanelInitialized = (): boolean => {
  try {
    mixpanel.get_distinct_id();
    return true;
  } catch {
    return false;
  }
};

const buildCropProps = (p: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
}) => ({
  crop_name: p.crop,
  currency: p.currency,
  price_value: p.price,
  quarter_name: p.quarter,
});

const trackEvent = (
  eventName: EventNameType,
  properties?: Record<string, unknown>,
) => {
  if (!isMixpanelInitialized()) {
    if (import.meta.env.DEV) {
      logger.log("[Mixpanel] Skip event (not init):", eventName);
    }
    return;
  }
  mixpanel.track(eventName, properties);
};

const trackQuarterClick = (params: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
  isFavorite: boolean;
}) => {
  trackEvent(EventName.QuarterClicked, {
    ...buildCropProps(params),
    is_favorite: params.isFavorite,
  });
};

const trackTradingSellClick = (params: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
}) => {
  trackEvent(EventName.TradingSellClicked, {
    ...buildCropProps(params),
  });
};

const trackTradingSellConfirmed = (params: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
}) => {
  trackEvent(EventName.TradingSellConfirmed, {
    ...buildCropProps(params),
  });
};

const trackTradingSellCancelled = (params: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
}) => {
  trackEvent(EventName.TradingSellCancelled, {
    ...buildCropProps(params),
  });
};

const trackTradingOrderClick = (params: {
  crop: string;
  price: number;
  currency: string;
  quarter: string;
}) => {
  trackEvent(EventName.TradingOrderClicked, {
    ...buildCropProps(params),
  });
};

const trackMarketplaceItemClick = (params: { id: string }) => {
  trackEvent(EventName.MarketplaceItemClicked, {
    marketplace_id: params.id,
  });
};

const trackNewStorageClick = () => {
  trackEvent(EventName.NewStorageClicked);
};

const trackStorageClick = (params: { name: string }) => {
  trackEvent(EventName.StorageClicked, {
    storage_name: params.name,
  });
};

export {
  trackMarketplaceItemClick,
  trackNewStorageClick,
  trackQuarterClick,
  trackStorageClick,
  trackTradingOrderClick,
  trackTradingSellCancelled,
  trackTradingSellClick,
  trackTradingSellConfirmed,
};
